import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, finalize, switchMap } from 'rxjs/operators';

import { UserService } from './user.service';
import { AuthHTTPService } from './http';

import { AuthType } from '../../utils';
import { AppResult } from '../../models';
import { PermissionCode } from '../../enums';
import { environment } from '../../../../environments/environment';
import { jwtLocalStorage, userLocalStorage } from '../../constants';
import { JwtPacketModel, UserModel, LoginModel, ResetPasswordModel, TockenModel } from '../../models/auths';

@Injectable({
   providedIn: 'root',
})
export class AuthService implements OnDestroy {
   // private readonly fields
   private readonly unsubscribe: Subscription[] = [];

   // public fields
   isLoading$: Observable<boolean>;
   currentAuth$: Observable<AuthType>;
   isLoadingSubject: BehaviorSubject<boolean>;
   currentAuthSubject: BehaviorSubject<AuthType>;
   currentPremission$: Observable<PermissionCode[]>;
   currentPremissionSubject: BehaviorSubject<PermissionCode[]>;

   get currentAuthValue(): AuthType {
      if (this.currentAuthSubject.value) {
         return this.currentAuthSubject.value;
      }

      const auth = this.getAuthFromLocalStorage();
      this.currentAuthSubject.next(auth);
      return this.currentAuthSubject.value;
   }

   set currentAuthValue(auth: AuthType) {
      this.currentAuthSubject.next(auth);
   }

   get currentPremissionValue(): PermissionCode[] {
      if (this.currentPremissionSubject.value.length > 0) {
         return this.currentPremissionSubject.value;
      }

      const auth = this.getAuthFromLocalStorage();
      this.currentPremissionSubject.next(this.readFromToken(auth));
      return this.currentPremissionSubject.value;
   }

   set currentPremissionValue(auth: PermissionCode[]) {
      this.currentPremissionSubject.next(auth);
   }

   constructor(private readonly authHttpService: AuthHTTPService, private readonly userService: UserService, private readonly router: Router) {
      this.isLoadingSubject = new BehaviorSubject<boolean>(false);
      this.currentAuthSubject = new BehaviorSubject<AuthType>(undefined);
      this.currentAuth$ = this.currentAuthSubject.asObservable();
      this.currentPremissionSubject = new BehaviorSubject<PermissionCode[]>([]);
      this.currentPremission$ = this.currentPremissionSubject.asObservable();
      this.isLoading$ = this.isLoadingSubject.asObservable();
   }

   // public methods
   login(model: LoginModel): Observable<UserModel | undefined> {
      this.isLoadingSubject.next(true);
      return this.authHttpService.login(model).pipe(
         map((auth: AppResult<JwtPacketModel>) => {
            if (auth.result && auth.isSuccess) {
               auth.result.imageUrl = environment.imageUrl + auth.result.imageUrl;
               this.setAuthToLocalStorage(auth.result);
               this.currentAuthSubject.next(auth.result);
               this.currentPremissionSubject.next(this.readFromToken(auth.result));
               return auth.result;
            }
            return undefined;
         }),
         switchMap(() => this.userService.GetUserProfile(this.currentAuthValue?.userName ?? '')),
         catchError((err) => {
            console.error('Login error:', err);
            return of(undefined);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   resetPassword(model: ResetPasswordModel): Observable<boolean | undefined> {
      this.isLoadingSubject.next(true);
      return this.authHttpService.resetPassword(model).pipe(
         map((result: AppResult<string>) => {
            return result.isSuccess;
         }),
         catchError((err) => {
            console.error('resetPassword error:', err);
            return of(undefined);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   logout() {
      const auth = this.getAuthFromLocalStorage();
      if (!auth?.userName) {
         this.goToLogin();
         return undefined;
      }

      this.logoutSub(auth.userName).subscribe((result: boolean) => {
         if (result) {
            this.goToLogin();
         }
      });
   }

   refreshToken(): Observable<JwtPacketModel | undefined> {
      const auth = this.getAuthFromLocalStorage();
      if (!auth?.refershedToken || !auth?.userName) {
         return of(undefined);
      }

      return this.authHttpService.refreshToken({ token: auth.refershedToken, userName: auth.userName }).pipe(
         map((auth: AppResult<JwtPacketModel>) => {
            if (auth.result) {
               auth.result.imageUrl = environment.imageUrl + auth.result.imageUrl;
               this.setAuthToLocalStorage(auth.result);
               this.currentAuthSubject.next(auth.result);
               this.currentPremissionSubject.next(this.readFromToken(auth.result));
               return auth.result;
            } else {
               this.logout();
               return undefined;
            }
         }),
         catchError((err) => {
            const errors: string[] = err.error.errors;
            console.error('refreshToken error:', err);
            if (errors?.includes('Invalid Token')) {
               this.goToLogin();
            }
            return of(undefined);
         })
      );
   }

   // private methods
   private setAuthToLocalStorage(auth: JwtPacketModel): boolean {
      // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
      if (auth?.token && localStorage) {
         localStorage.setItem(jwtLocalStorage, JSON.stringify(auth));
         return true;
      }
      return false;
   }

   private readFromToken(auth: JwtPacketModel | undefined): PermissionCode[] {
      if (auth?.token) {
         const decodedHeader: TockenModel = JSON.parse(atob(auth.token.split('.')[1]));
         decodedHeader.permissionCodes = decodedHeader.permissions.split(',').map(x => parseInt(x, 10));
         return decodedHeader.permissionCodes;
      }
      return [];
   }

   private getAuthFromLocalStorage(): JwtPacketModel | undefined {
      try {
         const lsValue = localStorage?.getItem(jwtLocalStorage);
         if (!lsValue) {
            return undefined;
         }

         const authData = JSON.parse(lsValue);
         return authData;
      } catch (error) {
         console.error(error);
         return undefined;
      }
   }

   private goToLogin() {
      this.removeLocalStorageData();
      this.router.navigate(['/auth/login'], { replaceUrl: true });
   }

   private removeLocalStorageData() {
      localStorage?.removeItem(jwtLocalStorage);
      localStorage?.removeItem(userLocalStorage);
      sessionStorage.clear();
      this.currentAuthSubject.next(undefined);
      this.currentPremissionSubject.next([]);
   }

   private logoutSub(userName: string): Observable<boolean> {
      return this.authHttpService.logout(userName).pipe(
         map(() => true),
         catchError((err) => {
            console.error('LogOut error:', err);
            return of(false);
         })
      )
   }

   ngOnDestroy() {
      this.unsubscribe.forEach((sb) => sb.unsubscribe());
   }
}
